<template lang="pug">
    div.tw-w-full
      div(v-if="title" class="tw-text-sm sm:tw-text-base lg:tw-text-xl tw-font-semibold tw-text-primary_brand tw-mb-2") {{ title }}
      div.tw-w-full(v-if="data.seriesData.length") 
        highcharts.tw-w-full(:options="chartData")
      div(v-else class="tw-p-3 sm:tw-p-4 tw-text-xs sm:tw-text-sm lg:tw-text-base tw-text-red-800 tw-rounded-lg tw-bg-red-50") No data available in chart!
    </template>

<script>
import moment from "moment";

export default {
  name: "dynamicChart",
  props: ["data", "topMargin", "title", "height", "isLegend"],

  data() {
    return {
      chartData: {},
    };
  },

  watch: {
    data: {
      deep: true,
      handler() {
        this.initiate();
      },
    },
  },

  mounted() {
    this.initiate();
  },

  methods: {
    formatXAxis(dateArray) {
      const resultArray = [];
      for (const dateString of dateArray) {
        const dateObj1 = moment(dateString, "DD MMM YYYY", true);
        const dateObj2 = moment(dateString, "YYYY-MM-DD", true);
        if (dateObj1.isValid()) {
          resultArray.push(dateObj1.format("DD MMM"));
        } else if (dateObj2.isValid()) {
          resultArray.push(dateObj2.format("DD MMM"));
        } else {
          resultArray.push(dateString);
        }
      }
      return resultArray;
    },

    chartHeight() {
      if (this.height) {
        return this.height + "px";
      } else {
        return "60%";
      }
    },

    ChartMarginTop() {
      return window.screen.width < 600 ? 110 : this.topMargin || "50";
    },

    yTitleSize() {
      return window.innerWidth < 640 ? 9 : false;
    },

    legendFontSize() {
      if (window.innerWidth < 640) {
        return "12px";
      } else if (window.innerWidth < 1024) {
        return "14px";
      } else {
        return "16px";
      }
    },

    legendPosition() {
      return window.innerWidth < 640 ? "center" : "right";
    },

    symbolHeight() {
      return window.innerWidth < 640 ? 7 : 14;
    },

    symbolRadius() {
      return window.innerWidth < 640 ? 2 : 4;
    },

    abbreviateNumber(value) {
      if (window.innerWidth < 640) {
        return Intl.NumberFormat("en-US", {
          notation: "compact",
          maximumFractionDigits: 2,
        }).format(value);
      }
      return value.toLocaleString("en-US");
    },

    lineMarker() {
      if (window.innerWidth < 640) {
        return {
          enabled: true,
          radius: 1.5,
          states: {
            hover: {
              radiusPlus: 1,
              lineWidthPlus: 1,
            },
          },
        };
      } else {
        return {
          states: {
            hover: {
              radiusPlus: 3,
              lineWidthPlus: 3,
            },
          },
        };
      }
    },

    labelFontSize() {
      if (window.innerWidth < 640) {
        return 9;
      } else if (window.innerWidth < 1024) {
        return 10;
      } else {
        return 11;
      }
    },

    plotLegend() {
      if (this.isLegend) {
        return true;
      } else if (!this.isLegend) {
        return false;
      }
    },

    initiate() {
      const self = this;
      if (!self.data.seriesData.length) {
        return;
      }

      const seriesData = [
        {
          name: self.data.seriesData[0].name,
          yAxis: 0,
          color: "#47A694",
          borderWidth: 0,
          data: self.data.seriesData[0].data,
        },
      ];

      const yAxis = [
        {
          labels: {
            formatter: function () {
              return self.abbreviateNumber(this.value);
            },
          },
          lineWidth: 0,
          crosshair: true,
          gridLineWidth: 1,
          title: "",
        },
      ];

      if (self.data.seriesData.length > 1) {
        for (let i = 1; i < self.data.seriesData.length; i++) {
          seriesData.push({
            yAxis: self.data.seriesData[i].yAxis === 0 ? 0 : 1,
            lineWidth: 1,
            marker: self.lineMarker(),
            color: self.data.seriesData[i].color,
            name: self.data.seriesData[i].name,
            type: "line",
            data: self.data.seriesData[i].data,
          });

          yAxis.push({
            labels: {
              formatter: function () {
                return self.abbreviateNumber(this.value);
              },
            },
            title: "",
            opposite: true,
          });
        }
      }

      self.chartData = {
        chart: {
          marginTop: this.ChartMarginTop(),
          backgroundColor: "transparent",
          height: this.chartHeight(),
        },
        title: {
          text: "",
        },
        subtitle: {
          text: "",
        },
        credits: {
          enabled: false,
        },
        legend: {
          itemStyle: {
            fontSize: self.legendFontSize(),
          },
          enabled: self.plotLegend(),
          alignColumns: true,
          align: self.legendPosition(),
          y: 0,
          x: 0,
          squareSymbol: true,
          verticalAlign: "top",
          itemDistance: 35,
          margin: 0,
          padding: 0,
          symbolHeight: this.symbolHeight(),
          symbolWidth: this.symbolHeight(),
          symbolRadius: this.symbolRadius(),
          itemStyle: {
            fontFamily: "Poppins",
            fontWeight: 600,
            color: "#a1a1a1",
            // fontSize: "14px",
            letterSpacing: ".5px",
            textTransform: "capitalize",
          },
        },
        xAxis: [
          {
            categories: self.formatXAxis(self.data.xAxis),
            crosshair: true,
            tickWidth: 1,
            tickmarkPlacement: "on",
            labels: {
              rotation: false,
              style: {
                fontSize: self.labelFontSize(),
              },
            },
          },
        ],
        yAxis: yAxis,
        tooltip: {
          shared: true,
          formatter: function () {
            let mytooltip = "<b>" + this.points[0].key + "</b>";
            let rs = " ";

            for (let i = 0; i < this.points.length; i++) {
              mytooltip +=
                "<br/>" +
                '<span style="color:' +
                this.points[i].series.color +
                '">●</span> ' +
                this.points[i].series.name +
                ": <b>" +
                rs +
                this.points[i].y.toLocaleString("en-US") +
                "</b>";
            }
            return mytooltip;
          },
          borderWidth: 0,
          shadow: {
            color: "#d6d6d6",
            opacity: 0.1,
            width: 10,
          },
          borderRadius: 6,
          backgroundColor: "#ffffff",
        },
        plotOptions: {
          column: {
            borderRadius: 5,
          },
          bar: {
            borderRadius: 8,
            dataLabels:{
              enabled:true
            },
            groupPadding:0.1
          },
        },
        series: this.data?.seriesData,
      };
    },
  },
};
</script>

<style scoped>
@media (max-width: 1400px) and (min-width: 1280px) {
  .adjustHeight {
    height: 350px !important;
  }
}

@media (max-width: 1024px) {
  .adjustHeight {
    height: 300px !important;
  }
}

@media (max-width: 768px) {
  .adjustHeight {
    height: 250px !important;
  }
}

@media (max-width: 640px) {
  .adjustHeight {
    height: 200px !important;
  }
}
</style>
